import React, { useEffect } from 'react'

import {
    Theme,
    Typography,
    Grid,
    Box,
    CircularProgress,
} from '@material-ui/core'
import { Container } from '../../../components'
import { LeaseAuditController } from '../../../hooks/documentAudit'
import {
    TermValues,
    AnalysisData,
    CriteriaGroup,
} from '../../../models/DocumentAudit/types'
import { normalizeString } from '../../../helpers/stringHelpers'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    theme: Theme
    leaseAuditController: LeaseAuditController
    criteriaGroup?: CriteriaGroup
}

export const AnalysisPage = (props: Props) => {
    const { theme, leaseAuditController } = props

    if (leaseAuditController.loading.fetchAnalysisData) {
        return (
            <Container>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                >
                    <CircularProgress />
                </Box>
            </Container>
        )
    }

    if (!leaseAuditController.analysisData) {
        return (
            <Container>
                <Typography>No analysis data available</Typography>
            </Container>
        )
    }

    return (
        <Container>
            <EntityClassTable
                theme={theme}
                data={leaseAuditController.analysisData}
                criteriaGroup={props.criteriaGroup}
            />
        </Container>
    )
}

interface EntityClassTableProps {
    theme: Theme
    data: AnalysisData
    criteriaGroup?: CriteriaGroup
}

const EntityClassTable = (props: EntityClassTableProps) => {
    const { theme, data, criteriaGroup } = props

    console.log('analysis data', data)

    const calculateVariance = (values: TermValues | undefined) => {
        if (!values) return 0
        return (values.rent_roll ?? 0) - (values.lease ?? 0)
    }

    const calculateTotalValues = (
        paired: TermValues | undefined,
        unpaired: TermValues | undefined,
    ): TermValues => {
        return {
            rent_roll: (paired?.rent_roll ?? 0) + (unpaired?.rent_roll ?? 0),
            lease: (paired?.lease ?? 0) + (unpaired?.lease ?? 0),
        }
    }

    const formatCurrency = (value: number | undefined | null) => {
        if (value === undefined || value === null) return '$0'
        // Round to nearest integer
        const roundedValue = Math.round(value)
        if (roundedValue < 0) {
            return `($${Math.abs(roundedValue).toLocaleString()})`
        }
        return `$${roundedValue.toLocaleString()}`
    }

    const PeriodValues = ({ values }: { values: TermValues | undefined }) => {
        if (!values) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    style={{
                        width: '100%',
                        padding: theme.spacing(0, 2),
                    }}
                >
                    <Typography
                        variant="body2"
                        style={{ marginBottom: theme.spacing(0.5) }}
                    >
                        {formatCurrency(0)}
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{ marginBottom: theme.spacing(0.5) }}
                    >
                        {formatCurrency(0)}
                    </Typography>
                    <Typography
                        variant="body2"
                        style={{
                            fontWeight: 'bold',
                            paddingTop: theme.spacing(0.5),
                            borderTop: `2px solid ${theme.palette.divider}`,
                        }}
                    >
                        {formatCurrency(0)}
                    </Typography>
                </Box>
            )
        }

        const variance = calculateVariance(values)

        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                style={{
                    width: '100%',
                    padding: theme.spacing(0, 2),
                }}
            >
                <Typography
                    variant="body2"
                    style={{ marginBottom: theme.spacing(0.5) }}
                >
                    {formatCurrency(values.rent_roll)}
                </Typography>
                <Typography
                    variant="body2"
                    style={{ marginBottom: theme.spacing(0.5) }}
                >
                    {formatCurrency(values.lease)}
                </Typography>
                <Typography
                    variant="body2"
                    style={{
                        fontWeight: 'bold',
                        paddingTop: theme.spacing(0.5),
                        borderTop: `2px solid ${theme.palette.divider}`,
                    }}
                >
                    {formatCurrency(variance)}
                </Typography>
            </Box>
        )
    }

    return (
        <Box>
            {/* Main headers row */}
            <Grid container>
                {/* Entity column - 2 spaces */}
                <Grid item xs={2}>
                    <Box
                        height="50px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    ></Box>
                </Grid>

                {/* Labels column - 1 space */}
                <Grid item xs={1}>
                    <Box
                        height="50px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    ></Box>
                </Grid>

                {/* Paired section header - 3 spaces */}
                <Grid item xs={3}>
                    <Box display="flex" justifyContent="center" padding={1}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                            Paired
                        </Typography>
                    </Box>
                </Grid>

                {/* Unpaired section header - 3 spaces */}
                <Grid
                    item
                    xs={3}
                    style={{ backgroundColor: theme.palette.grey[200] }}
                >
                    <Box display="flex" justifyContent="center" padding={1}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                            Unpaired
                        </Typography>
                    </Box>
                </Grid>

                {/* Total section header - 3 spaces */}
                <Grid
                    item
                    xs={3}
                    style={{ backgroundColor: theme.palette.grey[100] }}
                >
                    <Box display="flex" justifyContent="center" padding={1}>
                        <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                            Total
                        </Typography>
                    </Box>
                </Grid>
            </Grid>

            {/* Sub-headers row */}
            <Grid container style={{ marginTop: theme.spacing(1) }}>
                {/* Entity column - 2 spaces */}
                <Grid item xs={2}></Grid>

                {/* Labels column - 1 space */}
                <Grid item xs={1}></Grid>

                {/* Paired sub-headers - 3 spaces total */}
                <Grid item xs={3} style={{ padding: 0 }}>
                    <Grid container>
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Current
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Future
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Past
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Unpaired sub-headers - 3 spaces total */}
                <Grid item xs={3} style={{ padding: 0 }}>
                    <Grid
                        container
                        style={{ backgroundColor: theme.palette.grey[200] }}
                    >
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Current
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Future
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Past
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Total sub-headers - 3 spaces total */}
                <Grid item xs={3} style={{ padding: 0 }}>
                    <Grid
                        container
                        style={{ backgroundColor: theme.palette.grey[100] }}
                    >
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Current
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Future
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={4} style={{ padding: theme.spacing(1) }}>
                            <Box display="flex" justifyContent="center">
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    Past
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Data Rows */}
            {Object.entries(data).map(([entityClass, unitTypes], index) => (
                <Box
                    key={entityClass}
                    style={{
                        marginBottom: theme.spacing(1),
                        borderRadius: theme.shape.borderRadius,
                    }}
                >
                    <Grid container>
                        {/* Entity Class Title - 2 spaces */}
                        <Grid item xs={2} style={{ padding: theme.spacing(2) }}>
                            <Typography
                                variant="h6"
                                style={{
                                    fontWeight: 600,
                                    color: theme.palette.primary.main,
                                }}
                            >
                                {normalizeString(entityClass)}
                            </Typography>
                        </Grid>

                        {/* Labels Column - 1 space */}
                        <Grid item xs={1} style={{ padding: theme.spacing(2) }}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                style={{ gap: theme.spacing(0.5) }}
                            >
                                <Typography
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Rent Roll
                                </Typography>
                                <Typography
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Lease
                                </Typography>
                                <Typography
                                    style={{
                                        ...typographyStyle(
                                            theme.typography.body2,
                                        ),
                                        fontWeight: 'bold',
                                        paddingTop: theme.spacing(0.5),
                                        borderTop: `2px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    Variance
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Paired Values - 3 spaces total */}
                        <Grid item xs={3} style={{ padding: 0 }}>
                            <Grid container>
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={unitTypes?.all?.paired?.current}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={unitTypes?.all?.paired?.future}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={unitTypes?.all?.paired?.past}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Unpaired Values - 3 spaces total */}
                        <Grid item xs={3} style={{ padding: 0 }}>
                            <Grid
                                container
                                style={{
                                    backgroundColor: theme.palette.grey[200],
                                }}
                            >
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={
                                            unitTypes?.all?.unpaired?.current
                                        }
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={
                                            unitTypes?.all?.unpaired?.future
                                        }
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={unitTypes?.all?.unpaired?.past}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Total Values - 3 spaces total */}
                        <Grid item xs={3} style={{ padding: 0 }}>
                            <Grid
                                container
                                style={{
                                    backgroundColor: theme.palette.grey[100],
                                }}
                            >
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={calculateTotalValues(
                                            unitTypes?.all?.paired?.current,
                                            unitTypes?.all?.unpaired?.current,
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={calculateTotalValues(
                                            unitTypes?.all?.paired?.future,
                                            unitTypes?.all?.unpaired?.future,
                                        )}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={4}
                                    style={{ padding: theme.spacing(2) }}
                                >
                                    <PeriodValues
                                        values={calculateTotalValues(
                                            unitTypes?.all?.paired?.past,
                                            unitTypes?.all?.unpaired?.past,
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            ))}
        </Box>
    )
}
